import { environment } from 'src/environments/environment';

/**
 * Leaves the SPA and loads `/login` as a brand new document.
 *
 * ── Why not `router.navigate(['/login']).then(() => window.location.reload())` ────────────
 *
 * Every path that sends a user back to the login screen used to do exactly that, in twelve
 * places. It looks like one action and is really two, with a gap between them that the user can
 * type into:
 *
 *   1. `router.navigate()` resolves as soon as Angular has swapped the router outlet, so the
 *      login form is rendered, focused and fully interactive.
 *   2. ONLY THEN is `reload()` called, and it has to fetch `index.html` over the network —
 *      `client/server-static.js` serves every `.html` as `no-store`, so no HTTP cache can
 *      answer it.
 *
 * On an office connection step 2 finishes in a few tens of milliseconds and nobody notices. On a
 * congested mobile link it takes seconds, and the browser keeps the OLD document interactive for
 * all of them. Users filled in the form and signed in during that window; the reload then
 * committed and discarded the document, and the login went with it. Some got as far as receiving
 * a token — the server recorded `login_success` — before the page vanished under them.
 *
 * One navigation has no such gap. `replace()` rather than `assign()` so a signed-out page is not
 * left on the back stack.
 *
 * Related, and both still worth having: `ngsw-config.json` caches `index.html` so the fetch is
 * usually local, and `UserService.setUserProfile` no longer clears storage when it is passed
 * null, so a login that survives a late reload is not undone by the login screen rebuilding.
 */
export function redirectToLogin(): void {
  // SSR / prerender: no `window` to navigate, and no session to end.
  if (typeof window === 'undefined') {
    return;
  }

  // Native builds use hash routing (`app-routing.module.ts` sets `useHash` under Capacitor), so
  // `/login` is not a real path — the app is served from a local index — and assigning only the
  // fragment would not reload. Two steps are acceptable here precisely because the assets are
  // local: there is no network round trip, so there is no window to race.
  if ((environment as any).isNative === true) {
    window.location.hash = '/login';
    window.location.reload();
    return;
  }

  window.location.replace('/login');
}
